import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import EntryOptionsSummary from '../trophy/EntryOptionsSummary';
import GenderChoice, { type EntryGender } from '../trophy/GenderChoice';
import { choosesTurn, chosenTurn } from '../trophy/entry-options';
import { useEntryOptions, type EntryOptionsFetcher } from '../trophy/useEntryOptions';
import { isUnderConsentAge } from '../../utils/consent-age';
import {
  TeamEntryValidationError,
  teamEntriesService,
  type TeamEntry,
  type TeamEntryInput,
} from '../../services/team-entries';

interface TeamEntryDialogProps {
  open: boolean;
  competitionSlug: string;
  /**
   * The squad the athlete is entered for: the card the form was opened from.
   * Never asked in the form — a team manager of two squads has two cards.
   */
  team: { id: string; name: string } | null;
  /** The row being corrected; null to enter somebody new. */
  entry: TeamEntry | null;
  /**
   * Whether a squad's row may hold the gender here: the organiser signed the
   * agreement that names it. Asked only then — a squad's row has no profile to
   * keep it on otherwise.
   */
  genderCollected: boolean;
  onClose: () => void;
  onSaved: (entry: TeamEntry, created: boolean) => void;
  /**
   * Back to the search over the squad's athletes from the other stages, where
   * the stage offers it and the form was reached through its "Nuovo atleta".
   */
  onSearch?: () => void;
}

interface FormState {
  name: string;
  surname: string;
  birthDate: string;
  /** One box: the athlete's own address from 14, the guardian's below. */
  address: string;
  gender: EntryGender | null;
  /** The turn picked, when the class is split over two or more. */
  roundId: string | null;
  declaration: boolean;
}

/** Where a refusal is shown: a field of the form, the turn, or the form as a whole. */
type ErrorSlot = keyof FormState | 'turn' | 'base';

function initialState(entry: TeamEntry | null): FormState {
  return {
    name: entry?.name ?? '',
    surname: entry?.surname ?? '',
    birthDate: entry?.birth_date ?? '',
    address: (entry?.minor ? entry?.guardian_email : entry?.email) ?? '',
    gender: entry?.gender === 'male' || entry?.gender === 'female' ? entry.gender : null,
    roundId: entry?.competition_round_id ?? null,
    declaration: false,
  };
}

/** Which box a server error belongs under. Both addresses are the one box. */
const SLOT_OF: Record<string, ErrorSlot> = {
  name: 'name',
  surname: 'surname',
  birth_date: 'birthDate',
  email: 'address',
  guardian_email: 'address',
  gender: 'gender',
  competition_round_id: 'turn',
  competition_round: 'turn',
  base: 'base',
};

/**
 * Entering — or correcting — one athlete of the squad: a name, a surname, a
 * date of birth and, where the organiser's agreement covers it, the gender of
 * the class they compete in. That is what a club's accompanying adult knows of
 * the people they bring, and what places them in a category.
 *
 * Before saving, the form shows what the entry will be — the **category** and
 * the **rounds** of it, with their day and time — read from the server as the
 * values are typed (the public `entry_options`, the same answer the athlete's
 * own dialog shows). When the category is split over two or more turns the
 * squad picks one, and can move it later from the same form. A class the desk
 * chose by hand stays as it is, whatever is typed, and the form says so.
 *
 * A row made before the gender was asked opens with the gender marked as
 * missing: it has to be given before the row can be saved again.
 *
 * An address is optional, and **one box** stands for two different things
 * depending on the date above it: from 14 it is the athlete's own, and they
 * are invited to register; under 14 it is a parent's, who is invited to open
 * the child's account. We never write to a child, so the label and the hint
 * change as soon as the date says which it is, and the value goes to the
 * server as whichever of the two it now is.
 *
 * The squad is never asked: the form opens from one squad's card. The
 * declaration is asked only when entering, since correcting a name gives no
 * new data.
 *
 * A row the squad **re-entered** from another stage (`reentered`) carries a
 * date of birth and an address the squad was never shown: the form corrects
 * only the names and books the turn, and its class and turns are read from
 * the squad's own `entry_options`, which works them out on the server.
 */
export default function TeamEntryDialog({
  open,
  competitionSlug,
  team,
  entry,
  genderCollected,
  onClose,
  onSaved,
  onSearch,
}: TeamEntryDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => initialState(entry));
  const [errors, setErrors] = useState<Partial<Record<ErrorSlot, string>>>({});
  const [saving, setSaving] = useState(false);

  // A fresh form each time the dialog opens, for whichever row it opens on.
  useEffect(() => {
    if (open) {
      setForm(initialState(entry));
      setErrors({});
    }
  }, [open, entry]);

  const creating = entry === null;
  const reentered = !!entry?.reentered;
  const minor = isUnderConsentAge(form.birthDate);
  const today = new Date().toISOString().slice(0, 10);

  // A re-entered row's class is the server's to work out: the date it depends
  // on is not here. One fetcher per row, so the hook asks again only for it.
  const rowId = reentered ? entry?.id ?? null : null;
  const rowOptions = useMemo<EntryOptionsFetcher | undefined>(
    () => (rowId ? (slug) => teamEntriesService.entryOptions(slug, { entry_id: rowId }) : undefined),
    [rowId]
  );

  // The category follows from the values as they are typed: asked once the
  // date is a real one and — where it is asked at all — the gender is given.
  const birthDate = form.birthDate.length === 10 && form.birthDate <= today ? form.birthDate : null;
  const { options, loading: optionsLoading, error: optionsError } = useEntryOptions(
    open ? competitionSlug : null,
    reentered
      ? { birthDate: null, gender: null }
      : birthDate && (!genderCollected || form.gender)
        ? {
            birthDate,
            gender: genderCollected ? form.gender : null,
            categoryId: entry?.category_chosen ? entry.category_id ?? null : null,
          }
        : null,
    rowOptions
  );
  const choosingTurn = choosesTurn(options);
  const chosenRound = chosenTurn(options, form.roundId);
  const turnsShown = !!options?.category && options.bookable_rounds.length > 0;

  const canSave =
    form.name.trim().length > 0 &&
    form.surname.trim().length > 0 &&
    (reentered || form.birthDate.length > 0) &&
    !!team &&
    (!creating || form.declaration) &&
    (reentered || !genderCollected || !!form.gender) &&
    !optionsLoading &&
    (!choosingTurn || !!chosenRound);

  const set = (changes: Partial<FormState>) => setForm((current) => ({ ...current, ...changes }));

  const message = (code: string | undefined, detail: string) =>
    code ? t(`teamEntries.errors.${code}`, { defaultValue: detail }) : detail;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave || saving || !team) return;

    const address = form.address.trim() || null;
    const turn = choosingTurn && chosenRound ? { competition_round_id: chosenRound } : {};
    const input: TeamEntryInput = reentered
      ? // Only what the squad was shown: the date and the address stay as the
        // earlier entry had them, and are the desk's to correct.
        { name: form.name.trim(), surname: form.surname.trim(), competition_team_id: team.id, ...turn }
      : {
          name: form.name.trim(),
          surname: form.surname.trim(),
          birth_date: form.birthDate,
          // Both sent, so a date that crossed 14 moves the address rather than
          // leaving the old one behind in the other column.
          email: minor ? null : address,
          guardian_email: minor ? address : null,
          competition_team_id: team.id,
          ...(genderCollected ? { gender: form.gender } : {}),
          ...turn,
        };

    setSaving(true);
    setErrors({});
    try {
      const saved = creating
        ? await teamEntriesService.create(competitionSlug, input, form.declaration)
        : await teamEntriesService.update(competitionSlug, entry.id, input);
      onSaved(saved, creating);
    } catch (error) {
      if (error instanceof TeamEntryValidationError && Object.keys(error.fields).length > 0) {
        const next: Partial<Record<ErrorSlot, string>> = {};
        Object.entries(error.fields).forEach(([field, { code, detail }]) => {
          next[SLOT_OF[field] ?? 'base'] = message(code, detail);
        });
        setErrors(next);
      } else {
        const coded = error as { code?: string; message?: string };
        setErrors({ base: message(coded.code, coded.message ?? t('teamEntries.errors.generic')) });
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field: ErrorSlot) =>
    errors[field] ? (
      <p className="text-xs text-destructive" data-testid={`team-entry-error-${field}`}>
        {errors[field]}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden p-4 sm:max-w-lg sm:p-6">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle>
            {creating
              ? t('teamEntries.dialog.createTitle', { team: team?.name ?? '' })
              : t('teamEntries.dialog.editTitle', { name: entry.full_name })}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm" data-testid="team-entry-description">
            {reentered
              ? t('teamEntries.reEntry.reenteredNote')
              : genderCollected
                ? t('teamEntries.dialog.description')
                : t('teamEntries.dialog.descriptionNoGender')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="min-w-0 space-y-4" data-testid="team-entry-form">
          {creating && onSearch && (
            <Button
              type="button"
              variant="link"
              className="h-auto gap-1.5 px-0 text-sm"
              onClick={onSearch}
              data-testid="team-entry-search-instead"
            >
              <ArrowLeft className="size-4" />
              {t('teamEntries.reEntry.searchInstead')}
            </Button>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="team-entry-name">{t('teamEntries.dialog.name')}</Label>
              <Input
                id="team-entry-name"
                value={form.name}
                onChange={(event) => set({ name: event.target.value })}
                autoComplete="off"
                required
              />
              {fieldError('name')}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="team-entry-surname">{t('teamEntries.dialog.surname')}</Label>
              <Input
                id="team-entry-surname"
                value={form.surname}
                onChange={(event) => set({ surname: event.target.value })}
                autoComplete="off"
                required
              />
              {fieldError('surname')}
            </div>
          </div>

          {!reentered && (
            <div className="space-y-1.5">
              <Label htmlFor="team-entry-birth-date">{t('teamEntries.dialog.birthDate')}</Label>
              <Input
                id="team-entry-birth-date"
                type="date"
                max={today}
                value={form.birthDate}
                onChange={(event) => set({ birthDate: event.target.value })}
                required
              />
              {fieldError('birthDate')}
            </div>
          )}

          {genderCollected && !reentered && (
            <fieldset className="space-y-1.5" data-testid="team-entry-gender">
              <legend className="text-sm font-medium">{t('teamEntries.dialog.gender')}</legend>
              <GenderChoice name="team-entry-gender" value={form.gender} onChange={(gender) => set({ gender })} />
              {/* A row made before the squad was asked: it is saved again only
                  with the gender in. */}
              {!creating && !entry.gender && !form.gender ? (
                <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="team-entry-gender-missing">
                  {t('teamEntries.dialog.genderMissing')}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('stageEntry.dialog.genderHint')}</p>
              )}
              {fieldError('gender')}
            </fieldset>
          )}

          <section className="min-w-0 space-y-2 rounded-lg border p-3" data-testid="team-entry-options">
            {options ? (
              <EntryOptionsSummary
                options={options}
                roundId={form.roundId}
                onRoundChange={(roundId) => set({ roundId })}
                turnError={errors.turn}
                name="team-entry-turn"
                keptHint={t('teamEntries.dialog.categoryChosen')}
              />
            ) : optionsLoading ? (
              <div className="space-y-2" data-testid="team-entry-options-loading">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="team-entry-options-pending">
                {genderCollected ? t('teamEntries.dialog.optionsPending') : t('teamEntries.dialog.optionsPendingNoGender')}
              </p>
            )}
            {/* No gender on a squad's row here: only the classes the date
                alone decides are found, the others are the desk's. */}
            {!genderCollected && !reentered && options && options.category_state !== 'by_organiser' && (
              <p className="text-xs text-muted-foreground" data-testid="team-entry-gender-not-collected">
                {t('teamEntries.dialog.genderNotCollected')}
              </p>
            )}
            {optionsError !== null && (
              <p className="text-xs text-destructive" data-testid="team-entry-options-error">
                {optionsError || t('teamEntries.dialog.optionsError')}
              </p>
            )}
            {errors.turn && !turnsShown && fieldError('turn')}
          </section>

          {!reentered && (
            <div className="space-y-1.5">
              <Label htmlFor="team-entry-address">
                {minor ? t('teamEntries.dialog.guardianEmail') : t('teamEntries.dialog.email')}
              </Label>
              <Input
                id="team-entry-address"
                type="email"
                value={form.address}
                onChange={(event) => set({ address: event.target.value })}
                autoComplete="off"
              />
              {errors.address ? (
                fieldError('address')
              ) : (
                <p className="text-xs text-muted-foreground" data-testid="team-entry-address-help">
                  {minor ? t('teamEntries.dialog.guardianEmailHelp') : t('teamEntries.dialog.emailHelp')}
                </p>
              )}
            </div>
          )}

          {creating && (
            <div className="flex items-start gap-3">
              <Checkbox
                id="team-entry-declaration"
                checked={form.declaration}
                onCheckedChange={(value) => set({ declaration: value === true })}
                className="mt-0.5"
              />
              <Label htmlFor="team-entry-declaration" className="text-sm font-normal leading-snug">
                {t('teamEntries.dialog.declaration')}
              </Label>
            </div>
          )}

          {errors.base && (
            <p className="text-sm text-destructive" data-testid="team-entry-error-base">
              {errors.base}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t('teamEntries.dialog.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave || saving}>
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {creating ? t('teamEntries.dialog.enter') : t('teamEntries.dialog.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
